import React from 'react';
import { StatsigProvider, useClientAsyncInit } from '@statsig/react-bindings';
import { env } from '@/env';
import { useRegistration } from '@/context/RegistrationContext';
import { FullScreenLoader } from './FullScreenLoader';
import { PilotGate } from './PilotGate';

/**
 * Statsig SDK bootstrap + pilot gate (RND-36404).
 *
 * Placement: must live inside RegistrationProvider so it can identify the user
 * with the existing `webregUserId`.
 *
 * When `STATSIG_CLIENT_KEY` is unset (local dev, tests, unconfigured envs) the
 * SDK never initializes and the app renders unchanged — see `StatsigGate`.
 */
const StatsigGateActive: React.FC<{ clientKey: string; children: React.ReactNode }> = ({
  clientKey,
  children,
}) => {
  const { webregUserId, sampleGroup, utmParams } = useRegistration();

  const { client } = useClientAsyncInit(
    clientKey,
    {
      userID: webregUserId,
      custom: { sampleGroup, ...(utmParams ?? {}) },
    },
    // Report the environment tier (development / staging / production) from the
    // Vite build mode — via the centralized `env` abstraction — so console gate
    // rules can target per-environment.
    { environment: { tier: env.MODE } },
  );

  return (
    <StatsigProvider client={client} loadingComponent={<FullScreenLoader />}>
      <PilotGate>{children}</PilotGate>
    </StatsigProvider>
  );
};

export const StatsigGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const clientKey = env.STATSIG_CLIENT_KEY;

  // No key → Statsig disabled, gate bypassed. Keeps dev/tests behaving as before.
  if (!clientKey) {
    return <>{children}</>;
  }

  return <StatsigGateActive clientKey={clientKey}>{children}</StatsigGateActive>;
};
